import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "@lib/i18n/navigation-pages-router";
import ahoy from "@lib/ahoy";
import gtm from "@lib/gtm";
import { analytics } from "@lib/trackers/analytics";
import useFlash from "@hooks/use-flash";
import { useCurrentUser, useSubscriptionStatus } from "@hooks/use-current-user";
import { useAddCoupon, useTunnelSteps, useInitializePlan } from "@hooks/use-tunnel-steps";
import Head from "@components/layout/Head";
import Meta from "@components/layout/Meta";
import Flashes from "@components/layout/Flashes";
import UserLangModal from "@components/views/UserLangModal";
import { getSubscriptionCoupon } from "@utils/call-api/coupons-call";
import redeemTypes from "@utils/constants/redeemTypes";
import PromotionalBanner from "./PromotionalBanner";
import TunnelStepsLayoutHeader from "./TunnelStepsLayoutHeader";
import { unauthorizedSubscriptionStatusesInTunnel } from "@utils/constants/unauthorizedSubscriptionStatusesInTunnel";

const TunnelStepsLayout = ({ currentSectionNumber, currentStepNumber, children }) => {
  const t = useTranslations("tunnel_steps");

  const addCoupon = useAddCoupon();
  const initializePlan = useInitializePlan();
  const { coupon, featuredPlan } = useTunnelSteps();
  const router = useRouter();
  const user = useCurrentUser();
  const subscriptionStatus = useSubscriptionStatus();
  const addFlash = useFlash();

  const tunnelInitialized = !!featuredPlan?.id;

  const [checked, setChecked] = useState(false);

  async function addRedeemCodeInSessionStorage() {
    if (coupon) return;

    const redeemCodeToken = router.query.promo_code || sessionStorage.getItem("redeemCodeToken");
    if (!redeemCodeToken) return;

    sessionStorage.setItem("redeemCodeToken", redeemCodeToken);

    const couponRes = await getSubscriptionCoupon(redeemCodeToken);
    if (!couponRes.error_code) {
      addCoupon({ ...couponRes, referringToken: redeemCodeToken });
    }
  }

  function sendTrackerData() {
    ahoy("startfunnel", {
      promo_code: router.query.promo_code || "",
    });
    gtm(
      {
        event: "startfunnel",
        promo_code: router.query.promo_code || "",
      },
      user,
    );
    analytics.track("startfunnel", {
      promo_code: router.query.promo_code || "",
    });
  }

  async function initializeTunnel() {
    addRedeemCodeInSessionStorage();

    if (!tunnelInitialized) {
      sendTrackerData();
      initializePlan();
    }
  }

  function sendReferringEvent() {
    analytics.track("funnelwithreferring", {
      referring_token: coupon.referringToken,
      referrer_name: coupon.first_name,
    });
  }

  useEffect(() => {
    initializeTunnel();
  }, []);

  useEffect(() => {
    if (coupon?.type === redeemTypes.referring && user && tunnelInitialized) {
      sendReferringEvent();
    }
  }, [user, coupon]);

  useEffect(() => {
    function forceRedirectFromTunnel() {
      if (unauthorizedSubscriptionStatusesInTunnel.includes(subscriptionStatus)) {
        router.push("/home").then(() => {
          addFlash(t("subscription_already_active"), { variant: "danger" });
        });
      }
    }

    if (!checked && !!user) {
      setChecked(true);
      forceRedirectFromTunnel();
    }
  }, [subscriptionStatus]);

  return (
    <>
      <Meta />
      <Head />
      {tunnelInitialized && <PromotionalBanner />}
      <TunnelStepsLayoutHeader
        currentSectionNumber={currentSectionNumber}
        currentStepNumber={currentStepNumber}
      />
      <Flashes />

      <section>{tunnelInitialized && children}</section>
      <UserLangModal />
    </>
  );
};

export default TunnelStepsLayout;
